import React, { useEffect, useState } from "react";
import { Home, Search, Ticket, User, Plus, User as UserIcon, Mail, Phone, Bell, Shield, CreditCard, LogOut } from "lucide-react";
import PanelShell from "../../components/PanelShell";
import { useAuth } from "@/context/AuthContext";
import { clientApi } from "@/lib/clientApi";

const navItems = [
  { to: "/cliente", label: "Inicio", icon: Home, end: true },
  { to: "/cliente/buscar", label: "Buscar", icon: Search },
  { to: "/cliente/historial", label: "Reservas", icon: Ticket },
  { to: "/cliente/perfil", label: "Perfil", icon: User },
];
const bottomNav = [
  { to: "/cliente", label: "Inicio", icon: Home, end: true },
  { to: "/cliente/buscar", label: "Buscar", icon: Search },
  { to: "/cliente/reservar", label: "Reservar", icon: Plus },
  { to: "/cliente/historial", label: "Viajes", icon: Ticket },
  { to: "/cliente/perfil", label: "Perfil", icon: User },
];

const ACTIONS = [
  { icon: Bell, label: "Notificaciones", desc: "Push, email, WhatsApp" },
  { icon: Shield, label: "Seguridad", desc: "Contraseña y 2FA" },
  { icon: CreditCard, label: "Métodos de pago", desc: "Yape, tarjetas, empresa" },
];

const ClientProfile = () => {
  const { user, logout } = useAuth();
  const [tripsCount, setTripsCount] = useState(null);

  useEffect(() => {
    clientApi
      .listReservations()
      .then(({ data }) => setTripsCount(data.filter((r) => r.status === "completado").length))
      .catch(() => setTripsCount(null));
  }, []);

  const memberSince = user?.creado_en
    ? new Date(user.creado_en).toLocaleDateString("es-PE", { month: "long", year: "numeric" })
    : "—";

  return (
    <PanelShell role="Cliente" navItems={navItems} bottomNavItems={bottomNav} userName={user?.full_name} userMeta={user?.email} testId="client-profile">
      <div className="max-w-5xl mx-auto px-4 lg:px-8 py-8 lg:py-12">
        <p className="text-[10px] uppercase tracking-brand text-[#C9A227] font-display">Perfil</p>
        <h1 className="font-display font-bold text-3xl uppercase tracking-tight mt-1">Tu cuenta ejecutiva</h1>

        {/* Profile card */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-[#1C1D20] border border-[#2E2F33] rounded-xl p-6 lg:p-8">
            <div className="flex items-center gap-5">
              <div className="w-20 h-20 rounded-full bg-[#C9A227]/15 border border-[#C9A227]/40 flex items-center justify-center text-[#C9A227] font-display font-bold text-2xl">
                {(user?.full_name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2)}
              </div>
              <div className="flex-1">
                <h2 className="font-display font-bold text-xl">{user?.full_name}</h2>
                <p className="text-xs text-[#A0A0A5] mt-1">Miembro desde {memberSince}</p>
              </div>
            </div>

            <div className="hr-gold my-6" />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Info icon={Mail} label="Correo" value={user?.email} />
              <Info icon={Phone} label="Teléfono" value={user?.phone || "No registrado"} />
              <Info icon={UserIcon} label="Viajes completados" value={tripsCount === null ? "—" : `${tripsCount} viajes`} />
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            {ACTIONS.map((a, i) => {
              const Ico = a.icon;
              return (
                <button key={i} className="w-full text-left bg-[#1C1D20] border border-[#2E2F33] hover:border-[#C9A227]/50 rounded-xl p-5 flex items-center gap-4 transition" data-testid={`profile-action-${i}`}>
                  <div className="w-10 h-10 rounded-lg border border-[#C9A227]/30 flex items-center justify-center">
                    <Ico size={16} className="text-[#C9A227]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-display font-semibold uppercase tracking-wide">{a.label}</p>
                    <p className="text-xs text-[#A0A0A5]">{a.desc}</p>
                  </div>
                </button>
              );
            })}

            <button onClick={logout} className="w-full text-left bg-[#1C1D20] border border-red-500/30 hover:border-red-500/60 rounded-xl p-5 flex items-center gap-4 transition text-red-300" data-testid="logout-action">
              <div className="w-10 h-10 rounded-lg border border-red-500/40 flex items-center justify-center"><LogOut size={16} /></div>
              <div>
                <p className="text-sm font-display font-semibold uppercase tracking-wide">Cerrar sesión</p>
                <p className="text-xs text-red-300/70">Salir de tu cuenta</p>
              </div>
            </button>
          </div>
        </div>
      </div>
    </PanelShell>
  );
};

const Info = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3">
    <div className="w-9 h-9 rounded-lg border border-[#2E2F33] flex items-center justify-center">
      <Icon size={14} className="text-[#C9A227]" />
    </div>
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-brand text-[#A0A0A5] font-display">{label}</p>
      <p className="text-sm text-[#F5F5F5] font-body truncate">{value}</p>
    </div>
  </div>
);

export default ClientProfile;
